/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { PreviewType } from 'client-code/previews';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { TOfferPreview } from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    ActionButton,
    Badge,
    ContentDescription,
    Credits,
    Divider,
    Duration,
    FirstAirsOrAired,
    Genres,
    ParentalControlRating,
    SeaEpEpisodeTitle,
    Separator,
    ShowcaseBanner,
} from '../ui-kit';
import { InfoCardProps } from './InfoCardProps';

/**
 * EpisodeInfoCard
 * Ref.: http://w3-engr.tivo.com/MyWorld/UserInterface/Hydra/Specs/STB/core_ux/components/components.html#episode_info_card
 * There is no line limit or truncation enforced for metadata display. Display all metadata
 * available for each part, for example, title, description, or actors list.
 * UX Ref. https://app.zeplin.io/project/6266f85e6716281480fdbbd8/screen/68c3da472426d6fbf0916e9a
 */
const EpisodeInfoCard: FunctionComponent<InfoCardProps> = ({
    highlight,
    isActionMode,
    metadata,
    userActions,
    properties,
}) => {
    const { offerPreview = {} } = metadata as TOfferPreview;
    const { contentPreview } = offerPreview;
    if (!contentPreview) return null;
    const { previewType } = contentPreview;
    if (previewType !== PreviewType.EpisodePreview) return null;

    const { episodePreviewImageUrl = '' } = contentPreview;
    const previewInfo = {
        ...offerPreview,
        ...contentPreview,
    };

    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const url: string = assetService.getEpisodeBannerImageUrlForVodContent(
        contentPreview.contentId || '',
    );

    const pcRatingNames = PreviewComponent.getParentalRatingNames(previewInfo?.internalRatingList);
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        contentPreview.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );

    const drawInfoCard = () => {
        return (
            <div className="info-card-container">
                {/* <div className="info-card metadata-only"> 
                to show metadata only in scroll mode */}
                <div className="info-card">
                    <div className="info-card-header">
                        <div
                            className={
                                properties?.hasItemsBefore
                                    ? 'left-nav-arrow'
                                    : 'left-nav-arrow hidden'
                            }
                        ></div>
                        {/* <div className="showcase-banner-metadata">
                        to switch between banner with metadata view */}
                        <div className="uikit showcase-banner-metadata banner-only">
                            <ShowcaseBanner
                                bannerUrl={url || episodePreviewImageUrl}
                                fallbackText={
                                    contentPreview.title || contentPreview.episodeTitle || ''
                                }
                            />
                        </div>
                        <div
                            className={
                                properties?.hasItemsAfter
                                    ? 'right-nav-arrow'
                                    : 'right-nav-arrow hidden'
                            }
                        ></div>
                    </div>
                    <div className="info-card-body">
                        <SeaEpEpisodeTitle
                            episodeTitle={contentPreview.title || contentPreview.episodeTitle}
                            episodeNumber={contentPreview.episodeNumber}
                            seasonNumber={contentPreview.seasonNumber}
                            quoted={false}
                            trailingSeaEp={true}
                        />
                        <Badge
                            text={
                                previewInfo.videoResolution || previewInfo.channel?.videoResolution
                            }
                            uppercase={true}
                        />
                        <Badge text={previewInfo.isNew ? 'New' : ''} />
                        <ParentalControlRating ratingNames={pcRatingNames} />
                        <Separator />
                        <Genres categoryLabel={previewInfo.categoryLabel} />
                        <Separator />
                        <Duration duration={previewInfo.duration} />
                        <Separator />
                        <FirstAirsOrAired firstAiredDate={previewInfo.firstAiredDate} />
                        <Credits creditString={previewInfo.creditString} lines={2} />
                        <ContentDescription description={sContentDescription} />
                    </div>
                    <div className="info-card-footer">
                        <Divider />
                        {!isActionMode && <ActionButton type="options" highlighted={true} />}
                        {isActionMode &&
                            (userActions?.length > 0 ? (
                                userActions?.map((userAction, idx) => (
                                    <ActionButton
                                        key={userAction.id}
                                        highlighted={idx === highlight}
                                    >
                                        {userAction.text}
                                    </ActionButton>
                                ))
                            ) : (
                                <ActionButton type="info" highlighted={true} />
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    return <React.Fragment>{drawInfoCard()}</React.Fragment>;
};

export default EpisodeInfoCard;
